import React, { useMemo } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';


//This is a component to render markdown messages in the AI chat for better formatting for Erode AI.
type MarkdownBlock =
  | { type: 'heading'; level: 1 | 2 | 3 | 4; content: string }
  | { type: 'paragraph'; content: string }
  | { type: 'list'; ordered: boolean; items: ListItem[] }
  | { type: 'blockquote'; content: string }
  | { type: 'table'; header: string[]; rows: string[][] }
  | { type: 'code'; content: string }
  | { type: 'math'; content: string }
  | { type: 'divider' };

type ListItem = {// Type for list items, which may contain nested lists
  content: string;
  children?: { ordered: boolean; items: ListItem[] };
};

const listItemRegex = /^(\s*)([-*]|\d+\.)\s+(.*)$/; // Regex to match list items.

// Helper function to parse a table row into cells
const parseTableRow = (line: string) => {
  let row = line.trim();
  if (row.startsWith('|')) {
    row = row.slice(1);
  }
  if (row.endsWith('|')) {
    row = row.slice(0, -1);
  }
  return row.split('|').map((cell) => cell.trim());
};
// Helper function to check if a line is a table separator
const isTableSeparator = (line: string) =>
  /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(line);
// Recursive function to parse lists, including nested lists
const parseList = (lines: string[], startIndex: number) => {
  const startMatch = listItemRegex.exec(lines[startIndex]);
  if (!startMatch) { // If the starting line is not a list item, do not parse
    return null;
  }
  // Determine base indentation and list type
  const baseIndent = startMatch[1].length;
  const ordered = /\d+\./.test(startMatch[2]);
  const items: ListItem[] = [];
  let index = startIndex;
  // Parse list items. Handle nested lists recursively.
  while (index < lines.length) {
    const line = lines[index];
    if (line.trim() === '') {
      index += 1;
      continue;
    }

    const match = listItemRegex.exec(line);
    if (!match) {
      break;
    }

    const indent = match[1].length;
    const isOrdered = /\d+\./.test(match[2]);
    if (indent < baseIndent || isOrdered !== ordered) {
      break; // Stop parsing if indentation is less than base or list type changes
    }

    if (indent > baseIndent) {
      if (items.length > 0) {
        const nested = parseList(lines, index);
        if (nested) {
          items[items.length - 1].children = { ordered: nested.ordered, items: nested.items };
          index = nested.nextIndex;
          continue;
        }
      }
      break; // Stop parsing if unexpected indentation
    }

    items.push({ content: match[3] });
    index += 1;
  }

  return { ordered, items, nextIndex: index };
};

  // Function to parse markdown content into blocks
const parseMarkdownBlocks = (content: string): MarkdownBlock[] => {
  const blocks: MarkdownBlock[] = [];
  const lines = content.split('\n');
  let index = 0;
  let paragraphBuffer: string[] = [];

  // Helper functions to flush buffers
  const flushParagraph = () => {
    if (paragraphBuffer.length > 0) {
      blocks.push({ type: 'paragraph', content: paragraphBuffer.join('\n') });
      paragraphBuffer = [];
    }
  };
  // Main parsing loop. Handles different markdown elements.
  while (index < lines.length) {// Process each line
    const line = lines[index];
    // Horizontal rule: a line of only ---, ***, or ___ (3 or more)
    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) {
      flushParagraph();
      blocks.push({ type: 'divider' });
      index += 1;
      continue;
    }
    // Block math detection: $$...$$ on one line, or $$ ... $$ multiline.
    // flushParagraph() only fires inside a matched branch so malformed lines
    // (e.g. "$$foo" or "$$$$") fall through to normal paragraph handling
    // instead of triggering a spurious paragraph break.
    const trimmed = line.trim();
    if (trimmed.startsWith('$$')) {
      if (trimmed.endsWith('$$') && trimmed.length > 4) {
        flushParagraph();
        blocks.push({ type: 'math', content: trimmed.slice(2, -2) });
        index += 1;
        continue;
      }
      if (trimmed === '$$') {
        flushParagraph();
        const mathLines: string[] = [];
        index += 1;
        while (index < lines.length && lines[index].trim() !== '$$') {
          mathLines.push(lines[index]);
          index += 1;
        }
        if (index < lines.length) index += 1;
        blocks.push({ type: 'math', content: mathLines.join('\n') });
        continue;
      }
    }
    // Code block detection if the line starts with ```
    if (line.startsWith('```')) {
      flushParagraph();
      const codeLines: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].startsWith('```')) {
        codeLines.push(lines[index]);
        index += 1;
      }
      if (index < lines.length && lines[index].startsWith('```')) {
        index += 1;
      }
      blocks.push({ type: 'code', content: codeLines.join('\n') });
      continue;
    }
    // Table detection if the line contains '|' and the next line is a separator. 
    if (line.includes('|') && index + 1 < lines.length && isTableSeparator(lines[index + 1])) {
      flushParagraph();
      const header = parseTableRow(line);
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length && lines[index].includes('|') && !isTableSeparator(lines[index])) {
        rows.push(parseTableRow(lines[index]));
        index += 1;
      }
      blocks.push({ type: 'table', header, rows });
      continue;
    }
    // Blockquote detection if the line starts with '>'
    if (line.trim().startsWith('>')) {
      flushParagraph();
      const quoteLines: string[] = [];
      while (index < lines.length && lines[index].trim().startsWith('>')) {
        quoteLines.push(lines[index].replace(/^\s*>\s?/, ''));
        index += 1;
      }
      blocks.push({ type: 'blockquote', content: quoteLines.join('\n') });
      continue;
    }
    // Heading detection for levels 1-4
    const headingMatch = /^(#{1,4})\s+(.*)$/.exec(line);
    if (headingMatch) {
      flushParagraph();
      const level = headingMatch[1].length as 1 | 2 | 3 | 4;
      blocks.push({ type: 'heading', level, content: headingMatch[2] });
      index += 1;
      continue;
    }

    const listMatch = listItemRegex.exec(line); // List detection for ordered and unordered lists
    if (listMatch) { // If a list item is detected continue parsing the list
      flushParagraph();
      const list = parseList(lines, index);
      if (list) { // If a valid list is parsed, add it to blocks
        blocks.push({ type: 'list', ordered: list.ordered, items: list.items });
        index = list.nextIndex;
        continue;
      }
    }

    if (line.trim() === '') { // Empty line indicates paragraph break
      flushParagraph();
      index += 1;
      continue;
    }

    paragraphBuffer.push(line);
    index += 1;
  }

  flushParagraph();

  return blocks;
};

// Function to render inline markdown elements
const renderInline = (text: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = []; // Array to hold rendered nodes
  // Patterns for different inline markdown elements
  const patterns = [
    { type: 'link' as const, regex: /\[([^\]]+)\]\(([^)]+)\)/ },
    // Pandoc-style math delimiters: opening $ must be followed by a non-space,
    // closing $ must be preceded by a non-space and not followed by a digit.
    // This keeps currency like "$5 and one at $20" from parsing as math.
    { type: 'inlinemath' as const, regex: /(?<!\$)\$(?!\$)(?!\s)([^$\n]*?\S)\$(?!\$)(?!\d)/ },
    { type: 'code' as const, regex: /`([^`]+)`/ },
    { type: 'bold' as const, regex: /\*\*([^*]+)\*\*/ },
    { type: 'italic' as const, regex: /\*([^*]+)\*/ },
  ];

  let remaining = text;
  let key = 0;

  while (remaining.length > 0) { // Process remaining text to find inline elements
    let earliest:
      | { type: 'link' | 'inlinemath' | 'code' | 'bold' | 'italic'; index: number; match: RegExpExecArray }
      | null = null;
      // Find the earliest match among all patterns
    for (const pattern of patterns) {
      const match = pattern.regex.exec(remaining);
      if (match && (earliest === null || match.index < earliest.index)) {
        earliest = { type: pattern.type, index: match.index, match };
      }
    }
    // If no more matches, push the remaining text and break
    if (!earliest) {
      nodes.push(remaining);
      break;
    }
    // Push text before the earliest match
    if (earliest.index > 0) {
      nodes.push(remaining.slice(0, earliest.index));
    }

    const [full, first, second] = earliest.match;
    // Render the matched inline element based on its type
    if (earliest.type === 'link') {
      nodes.push(
        <a key={`md-${key++}`} href={second} className="text-[#7BA428] underline decoration-[#7BA428]/30 hover:decoration-[#7BA428] transition-colors duration-200 font-medium" target="_blank" rel="noreferrer">
          {first}
        </a>
      );
    } else if (earliest.type === 'code') {
      nodes.push(
        <code key={`md-${key++}`} className="rounded-md bg-gray-100 border border-gray-200 px-1.5 py-0.5 font-mono text-xs text-gray-800">
          {first}
        </code>
      );
    } else if (earliest.type === 'bold') {
      // Recurse so nested inline elements (e.g. math like **$E$**) still render.
      nodes.push(<strong key={`md-${key++}`} className="font-semibold text-gray-900">{renderInline(first)}</strong>);
    } else if (earliest.type === 'italic') {
      // Recurse so nested inline elements (e.g. math like *$E$*) still render.
      nodes.push(<em key={`md-${key++}`} className="italic text-gray-700">{renderInline(first)}</em>);
    } else if (earliest.type === 'inlinemath') {
      // Safe: KaTeX compiles LaTeX to its own static DOM (no script execution),
      // so this dangerouslySetInnerHTML is not an XSS vector.
      const html = katex.renderToString(first, { displayMode: false, throwOnError: false });
      nodes.push(<span key={`md-${key++}`} className="whitespace-normal" dangerouslySetInnerHTML={{ __html: html }} />);
    }

    remaining = remaining.slice(earliest.index + full.length);
  }

  return nodes;
};
// Function to render paragraphs with inline elements
const renderParagraph = (content: string) => {
  const parts = content.split('\n');
  return parts.map((part, index) => (
    <React.Fragment key={`p-${index}`}>
      {renderInline(part)}
      {index < parts.length - 1 ? <br /> : null}
    </React.Fragment>
  ));
};
// Function to render lists, including nested lists
const renderList = (items: ListItem[], ordered: boolean, keyPrefix: string, nested = false) => {
  const ListTag = ordered ? 'ol' : 'ul';
  const listClass = ordered ? 'list-decimal' : 'list-disc';
  const spacingClass = nested ? 'mt-1.5 pl-5' : 'pl-5 mb-3 last:mb-0';
  return (
    <ListTag className={`${listClass} ${spacingClass} marker:text-gray-500`} key={keyPrefix}>
      {items.map((item, itemIndex) => (
        <li key={`${keyPrefix}-${itemIndex}`} className="mb-1.5 last:mb-0 leading-relaxed text-gray-700">
          {renderInline(item.content)}
          {item.children ? renderList(item.children.items, item.children.ordered, `${keyPrefix}-${itemIndex}`, true) : null}
        </li>
      ))}
    </ListTag>
  );
};
// Main MarkdownMessage component
interface MarkdownMessageProps {
  content: string;
  className?: string;
}

const MarkdownMessage: React.FC<MarkdownMessageProps> = ({ content, className }) => {
  // Memoize so streaming responses don't re-parse the whole message on every token tick.
  const blocks = useMemo(() => parseMarkdownBlocks(content), [content]);
  return (
    // Render the parsed markdown blocks depending on their type
    <div className={`markdown text-sm ${className ?? ''}`.trim()}>
      {blocks.map((block, index) => {
        if (block.type === 'heading') {
          if (block.level === 1) {
            return <h1 key={`md-${index}`} className="!text-xl !font-extrabold mb-3 mt-1 text-gray-900 tracking-tight">{renderInline(block.content)}</h1>;
          }
          if (block.level === 2) {
            return <h2 key={`md-${index}`} className="!text-lg !font-bold mb-2.5 mt-1 text-gray-800">{renderInline(block.content)}</h2>;
          }
          if (block.level === 3) {
            return <h3 key={`md-${index}`} className="!text-base !font-semibold mb-2 text-gray-700">{renderInline(block.content)}</h3>;
          }
          return <h4 key={`md-${index}`} className="!text-sm !font-medium mb-2 text-gray-600 uppercase tracking-wider">{renderInline(block.content)}</h4>;
        }

        if (block.type === 'list') {
          return renderList(block.items, block.ordered, `md-${index}`);
        }

        if (block.type === 'blockquote') {
          return (
            <blockquote key={`md-${index}`} className="border-l-4 border-[#7BA428]/30 pl-4 py-1 text-gray-700 mb-3 last:mb-0 bg-[#7BA428]/5 rounded-r-md italic">
              {renderParagraph(block.content)}
            </blockquote>
          );
        }

        if (block.type === 'table') {
          return (
            <div key={`md-${index}`} className="my-3 overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100 text-gray-800 border-b border-gray-200">
                    {block.header.map((cell, cellIndex) => (
                      <th key={`md-${index}-h-${cellIndex}`} className="px-3 py-2 text-left font-semibold">
                        {renderInline(cell)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, rowIndex) => (
                    <tr key={`md-${index}-r-${rowIndex}`} className="even:bg-gray-50/50 hover:bg-[#7BA428]/5 transition-colors duration-150 border-b border-gray-100 last:border-0">
                      {row.map((cell, cellIndex) => (
                        <td key={`md-${index}-r-${rowIndex}-c-${cellIndex}`} className="px-3 py-2 text-gray-700">
                          {renderInline(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        if (block.type === 'code') {
          return (
            <div key={`md-${index}`} className="my-3 group relative">
              <pre className="max-w-full overflow-x-auto rounded-lg bg-gray-900 p-4 text-xs text-gray-100 shadow-md border border-gray-800">
                <code className="font-mono leading-relaxed">{block.content}</code>
              </pre>
            </div>
          );
        }

        if (block.type === 'math') {
          // Safe: KaTeX compiles LaTeX to its own static DOM (no script execution),
          // so this dangerouslySetInnerHTML is not an XSS vector.
          const html = katex.renderToString(block.content, { displayMode: true, throwOnError: false });
          return (
            // max-w-full + overflow-x-auto keep wide equations from forcing the chat
            // layout wider; whitespace-normal resets the bubble's inherited pre-wrap,
            // which otherwise distorts KaTeX's own whitespace handling; the
            // [&_.katex-display]:my-0 override drops KaTeX's built-in 1em margins so
            // only our my-2 spacing applies (keeps math from inflating message height).
            <div key={`md-${index}`} className="my-2 max-w-full overflow-x-auto whitespace-normal text-center py-1 [&_.katex-display]:my-0" dangerouslySetInnerHTML={{ __html: html }} />
          );
        }

        if (block.type === 'divider') {
          return <hr key={`md-${index}`} className="my-4 border-t border-gray-200" />;
        }

        return (
          <p key={`md-${index}`} className="mb-3 last:mb-0 leading-relaxed text-gray-700 text-sm">
            {renderParagraph(block.content)}
          </p>
        );
      })}
    </div>
  );
};

export default MarkdownMessage;
